import type { ParsedUrlQuery } from "querystring";

export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"] as const;

export type UtmKey = (typeof UTM_KEYS)[number];

export type UtmParams = Partial<Record<UtmKey, string>>;

const STORAGE_KEY = "simcorp_utm";

const isBrowser = (): boolean => typeof window !== "undefined";

const normalize = (value: string | string[] | undefined): string | undefined =>
    Array.isArray(value) ? value[0] : value;

/**
 * Extract the UTM params that are present in the current URL query.
 * Deterministic and SSR-safe, so it can seed the initial client render.
 */
export const pickUtmFromQuery = (query: ParsedUrlQuery): UtmParams => {
    const result: UtmParams = {};
    UTM_KEYS.forEach((key) => {
        const value = normalize(query[key]);
        if (value) result[key] = value;
    });
    return result;
};

export const getStoredUtm = (): UtmParams => {
    if (!isBrowser()) return {};
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as UtmParams;
        const result: UtmParams = {};
        UTM_KEYS.forEach((key) => {
            if (typeof parsed[key] === "string") result[key] = parsed[key];
        });
        return result;
    } catch {
        return {};
    }
};

/**
 * Persist UTM params from the URL into sessionStorage. Only keys present in the
 * URL are written, so a later page with a clean URL never clears earlier values.
 * sessionStorage is tab-scoped and cleared when the tab closes.
 */
export const saveUtmFromQuery = (query: ParsedUrlQuery): void => {
    if (!isBrowser()) return;
    const fromQuery = pickUtmFromQuery(query);
    if (Object.keys(fromQuery).length === 0) return;
    try {
        const merged = { ...getStoredUtm(), ...fromQuery };
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch {
        // Ignore write failures (private mode / storage quota).
    }
};

/**
 * Effective UTM params for the current request: the URL query wins per key,
 * with stored values filling in any keys missing from the current URL.
 */
export const getEffectiveUtm = (query: ParsedUrlQuery): UtmParams => ({
    ...getStoredUtm(),
    ...pickUtmFromQuery(query),
});
